<template>
  <v-dialog
    :value="showDialog"
    max-width="900"
    scrollable
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card>
      <v-card-title>
        Gestion des accès
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-container class="px-0">
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-card
                elevation="0"
                outlined
                rounded="lg"
              >
                <v-text-field
                  v-model="searchCollaborators"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Rechercher un collaborateur"
                  @input="debouncedSearch"
                  @click:clear="clearSearch"
                />

                <v-list
                  dense
                  class="flex-grow-1 overflow-y-auto"
                  style="max-height: 400px; min-height: 400px;"
                >
                  <template v-if="isLoading">
                    <v-skeleton-loader type="list-item@8" />
                  </template>
                  <template v-else-if="searchResults.length === 0 && hasSearched">
                    <div class="text-center grey--text mt-10">Aucun résultat</div>
                  </template>
                  <template v-else-if="searchResults.length === 0 && !hasSearched">
                    <div class="text-center grey--text mt-10">Recherchez un collaborateur</div>
                  </template>

                  <template v-else>
                    <v-list-item
                      v-for="(collaborator, collaboratorIndex) in searchResults"
                      :key="collaboratorIndex"
                      @click="addCollaborator(collaborator)"
                    >
                      <v-list-item-action class="mr-2">
                        <v-icon color="success">mdi-plus-circle-outline</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="text-capitalize">{{ collaboratorFullName(collaborator) }}</v-list-item-title>
                        <v-list-item-subtitle>{{ collaborator.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card>
            </v-col>

            <!-- Right Column: Selected Collaborators -->
            <v-col cols="12" md="6">
              <v-card
                elevation="0"
                outlined
                rounded="lg"
              >
                <v-text-field
                  v-model="searchSelectedCollaborators"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Filtrer la sélection"
                />

                <v-list
                  dense
                  class="flex-grow-1 overflow-y-auto"
                  style="max-height: 400px; min-height: 400px;"
                >
                  <template v-if="value.length === 0">
                    <div class="text-center grey--text mt-10">Aucun collaborateur sélectionné</div>
                  </template>
                  <template v-else>
                    <v-list-item
                      v-for="(selectedCollaborator, selectedCollaboratorIndex) in filteredSelected"
                      :key="selectedCollaboratorIndex"
                      @click="removeCollaborator(selectedCollaborator)"
                    >
                      <v-list-item-action class="mr-2">
                        <v-icon color="error">mdi-minus-circle-outline</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title class="text-capitalize">{{ selectedCollaborator.fullName }}</v-list-item-title>
                        <v-list-item-subtitle>{{ selectedCollaborator.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Fermer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { filterListBySearch } from '@/configs/helper';
import { useCollaboratorSearch } from '@/composables/useCollaboratorSearch';

const props = defineProps({
  showDialog: {
    type: Boolean,
    default: false
  },
  value: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['input', 'update:show-dialog']);

const searchCollaborators = ref('');
const searchSelectedCollaborators = ref('');

const {
  items: searchResults,
  isLoading,
  hasSearched,
  debouncedSearch: _debouncedSearch,
  clearSearch: _clearSearch
} = useCollaboratorSearch();

const collaboratorFullName = collaborator => `${collaborator.firstname} ${collaborator.lastname}`;

const filteredSelected = computed(() => {
  const list = props.value.map(i => ({
    ...i
    // name: collaboratorFullName(i)
  }));
  return filterListBySearch(list, searchSelectedCollaborators.value);
});

const close = () => {
  emit('update:show-dialog', false);
};

const debouncedSearch = val => {
  const excluded = props.value.map(c => c?.email || c);
  _debouncedSearch(val, { size: 50, excludeEmails: excluded });
};

const clearSearch = () => {
  searchCollaborators.value = '';
  _clearSearch();
};

const addCollaborator = item => {
  const collaboratorToAdd = {
    email: item.email,
    fullName: item.fullName || `${item.firstname} ${item.lastname}`
  };
  const newValue = [...props.value, collaboratorToAdd];
  searchResults.value = searchResults.value.filter(i => i.email !== item.email);
  emit('input', newValue);
};

const removeCollaborator = item => {
  const newValue = props.value.filter(i => i.email !== item.email);
  emit('input', newValue);
};
</script>
